// Material UI v4 - Components
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  Box,
  Button,
  TextField,
  Card,
  CardContent,
  CircularProgress,
} from "@material-ui/core";

// Material UI v4 - Icons
import { Undo as UndoIcon } from "@material-ui/icons";

// React
import { useState, useEffect } from "react";

// RTK Query
import { useRevertirConciliacionMutation } from "src/services/conciliacionApi";

// Helpers
import { formatearMonto } from "src/utils/conciliacionHelpers";

// Snackbar
import { useSnackbar } from "src/components/snackbar";

// ==================== COMPONENTE PRINCIPAL ====================
export default function DialogRevertirConciliacion({
  open,
  onClose,
  conciliacion,
}) {
  // ==================== ESTADOS ====================
  const [motivo, setMotivo] = useState("");

  // ==================== HOOKS ====================
  const [revertirConciliacion, { isLoading: isRevirtiendo }] =
    useRevertirConciliacionMutation();

  const { enqueueSnackbar } = useSnackbar();

  // ==================== EFECTOS ====================

  // Limpiar motivo al abrir/cerrar el diálogo
  useEffect(() => {
    if (open) {
      setMotivo("");
    }
  }, [open]);

  // ==================== HELPERS ====================

  const truncarTexto = (texto, maxLength = 50) => {
    if (!texto) return "-";
    return texto.length > maxLength
      ? `${texto.substring(0, maxLength)}...`
      : texto;
  };

  // ==================== HANDLERS ====================

  const handleClose = () => {
    if (!isRevirtiendo) {
      setMotivo("");
      onClose();
    }
  };

  const handleMotivoChange = (event) => {
    setMotivo(event.target.value);
  };

  const handleConfirmarRevertir = async () => {
    if (!conciliacion) return;

    try {
      const resultado = await revertirConciliacion({
        conciliacion_id: conciliacion.id,
        motivo: motivo || "Sin motivo especificado",
      }).unwrap();

      enqueueSnackbar(
        resultado.mensaje || "Conciliación revertida exitosamente",
        { variant: "success" },
      );

      // Limpiar y cerrar
      setMotivo("");
      onClose();
    } catch (error) {
      console.error("Error al revertir conciliación:", error);
      enqueueSnackbar(
        error?.data?.message || "Error al revertir la conciliación",
        { variant: "error" },
      );
    }
  };

  // ==================== VALIDACIONES ====================

  if (!conciliacion) {
    return null;
  }

  // ==================== RENDER ====================

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Typography variant="h6" component="div" style={{ color: "#f44336" }}>
            ¿Confirmar Reversión de Conciliación?
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <DialogContentText>
          Esta acción revertirá la conciliación ID:{" "}
          <strong>{conciliacion.id}</strong>
        </DialogContentText>

        {/* INFORMACIÓN DEL MOVIMIENTO */}
        <Box mt={2} mb={2}>
          <Card variant="outlined" style={{ backgroundColor: "#FFF3E0" }}>
            <CardContent>
              <Typography variant="caption" color="textSecondary">
                Movimiento:
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatearMonto(conciliacion.movimiento?.monto)} -{" "}
                {truncarTexto(conciliacion.movimiento?.glosa, 50)}
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                style={{ marginTop: 8 }}
              >
                Facturas: {conciliacion.detalles?.length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <DialogContentText>
          El movimiento volverá a estado <strong>PENDIENTE</strong> y las
          facturas quedarán disponibles para nueva conciliación.
        </DialogContentText>

        {/* CAMPO DE MOTIVO */}
        <TextField
          autoFocus
          margin="dense"
          label="Motivo de reversión (opcional)"
          fullWidth
          multiline
          rows={3}
          value={motivo}
          onChange={handleMotivoChange}
          placeholder="Ej: Error en la conciliación, monto incorrecto..."
          variant="outlined"
          helperText="Este motivo quedará registrado en la auditoría"
          disabled={isRevirtiendo}
        />
      </DialogContent>

      {/* ACCIONES */}
      <DialogActions>
        <Button onClick={handleClose} disabled={isRevirtiendo}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirmarRevertir}
          color="secondary"
          variant="contained"
          disabled={isRevirtiendo}
          startIcon={
            isRevirtiendo ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <UndoIcon />
            )
          }
        >
          {isRevirtiendo ? "Revirtiendo..." : "Confirmar Reversión"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
